/**
 * Vesper DM Chat Hook (Shared)
 *
 * Purpose: Encapsulates real-time DM/group chat over Socket.io.
 * Handles room join/leave, incoming messages, typing indicators,
 * and message sending — all with proper cleanup.
 *
 * Falls back to HTTP polling if socket is unavailable.
 *
 * Socket is injected as a parameter so both web and mobile can
 * supply their own Socket.io instance.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as dmService from '../services/dm.service';
import type { DMMessage } from '../types/dm.types';

/** Minimal Socket.io client interface (avoids hard dep on socket.io-client) */
export interface SocketLike {
  connected: boolean;
  emit(event: string, ...args: unknown[]): void;
  on(event: string, listener: (...args: unknown[]) => void): void;
  off(event: string, listener: (...args: unknown[]) => void): void;
}

interface TypingUser {
  userId: string;
  /** Timestamp when typing started — used to auto-expire */
  since: number;
}

export interface UseDMChatReturn {
  messages: DMMessage[];
  sendMessage: (content: string) => void;
  typingUsers: string[];
  isConnected: boolean;
  /** Notify others that this user is typing */
  sendTyping: () => void;
}

/** How long to show a typing indicator before auto-clearing (ms) */
const TYPING_TIMEOUT = 3_000;

/** HTTP polling interval when socket is disconnected (ms) */
const POLL_INTERVAL = 10_000;

export function useDMChat(
  conversationId: string | undefined,
  socket: SocketLike | null,
  currentUserId: string | undefined,
): UseDMChatReturn {
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [typingMap, setTypingMap] = useState<Map<string, TypingUser>>(new Map());
  const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const joinedRoom = useRef<string | null>(null);
  const isConnected = socket?.connected ?? false;

  // ── Load initial messages via HTTP ──────────────────────────────────────────
  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const msgs = await dmService.getDMMessages(conversationId);
      setMessages(msgs);
    } catch {
      // Silently fail — will retry on next poll or socket reconnect
    }
  }, [conversationId]);

  // Initial load
  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // ── HTTP polling fallback when socket is not connected ──────────────────────
  useEffect(() => {
    if (isConnected || !conversationId) return;

    const interval = setInterval(() => {
      void loadMessages();
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [isConnected, conversationId, loadMessages]);

  // ── Socket room management & event listeners ───────────────────────────────
  useEffect(() => {
    if (!socket || !conversationId) return;

    // Join room
    socket.emit('join-dm', conversationId);
    joinedRoom.current = conversationId;

    // Listen for new messages
    const handleNewMessage = (msg: DMMessage) => {
      if (msg.conversationId !== conversationId) return;
      setMessages((prev) => {
        // Deduplicate (in case HTTP poll and socket both deliver)
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    };

    // Listen for typing indicators
    const handleTyping = (data: { userId: string; conversationId: string }) => {
      if (data.conversationId !== conversationId || data.userId === currentUserId) return;

      setTypingMap((prev) => {
        const next = new Map(prev);
        next.set(data.userId, { userId: data.userId, since: Date.now() });
        return next;
      });

      // Clear existing timer for this user
      const existingTimer = typingTimers.current.get(data.userId);
      if (existingTimer) clearTimeout(existingTimer);

      // Auto-expire typing indicator
      const timer = setTimeout(() => {
        setTypingMap((prev) => {
          const next = new Map(prev);
          next.delete(data.userId);
          return next;
        });
        typingTimers.current.delete(data.userId);
      }, TYPING_TIMEOUT);

      typingTimers.current.set(data.userId, timer);
    };

    // Listen for stop-typing
    const handleStopTyping = (data: { userId: string; conversationId: string }) => {
      if (data.conversationId !== conversationId) return;
      setTypingMap((prev) => {
        const next = new Map(prev);
        next.delete(data.userId);
        return next;
      });
      const timer = typingTimers.current.get(data.userId);
      if (timer) {
        clearTimeout(timer);
        typingTimers.current.delete(data.userId);
      }
    };

    // Re-fetch messages on reconnect (in case we missed some)
    const handleReconnect = () => {
      socket.emit('join-dm', conversationId);
      void loadMessages();
    };

    socket.on('new-dm-message', handleNewMessage as (...args: unknown[]) => void);
    socket.on('dm-typing', handleTyping as (...args: unknown[]) => void);
    socket.on('dm-stop-typing', handleStopTyping as (...args: unknown[]) => void);
    socket.on('connect', handleReconnect);

    return () => {
      socket.emit('leave-dm', conversationId);
      joinedRoom.current = null;

      socket.off('new-dm-message', handleNewMessage as (...args: unknown[]) => void);
      socket.off('dm-typing', handleTyping as (...args: unknown[]) => void);
      socket.off('dm-stop-typing', handleStopTyping as (...args: unknown[]) => void);
      socket.off('connect', handleReconnect);

      // Clean up typing timers
      typingTimers.current.forEach((timer) => clearTimeout(timer));
      typingTimers.current.clear();
      setTypingMap(new Map());
    };
  }, [socket, conversationId, currentUserId, loadMessages]);

  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    (content: string) => {
      if (!conversationId || !content.trim()) return;

      if (socket?.connected) {
        // Real-time path: emit to server, which broadcasts back to everyone
        socket.emit('send-dm-message', { conversationId, content: content.trim() });
        socket.emit('dm-stop-typing', { conversationId });
      } else {
        // Fallback: use HTTP API and poll for the result
        void dmService.sendDMMessage(conversationId, content.trim()).then(() => {
          void loadMessages();
        });
      }
    },
    [socket, conversationId, loadMessages],
  );

  // ── Send typing indicator ──────────────────────────────────────────────────
  const sendTyping = useCallback(() => {
    if (!socket?.connected || !conversationId) return;
    socket.emit('dm-typing', { conversationId });
  }, [socket, conversationId]);

  return {
    messages,
    sendMessage,
    typingUsers: Array.from(typingMap.keys()),
    isConnected,
    sendTyping,
  };
}
